<template>
  <div class="card pb-4">
    <div class="row mb-3">
      <div class="col-6 align-self-center">
        <h2 class="subtitle mb-0">
          API Keys
          <el-popover placement="top" trigger="hover" width="300">
            <p class="form-control-label mb-2">
              <strong>What is a Reseller API Key?</strong>
            </p>
            <p class="text-justify f1 lh-f1 mb-0">
              A unique credential used to authenticate and authorize the
              reseller's access to the API. Store it securely — it is only shown
              once upon generation.
            </p>
            <div class="badge badge-primary" slot="reference">?</div>
          </el-popover>
        </h2>
      </div>
      <div class="col-6 text-right align-self-center">
        <base-button
          type="outline-secondary"
          class="min-button"
          :disabled="generating"
          @click="generateApiKey()"
        >
          <b-spinner v-if="generating" small class="mr-1" />
          <i v-else class="flaticon-plus"></i>
          {{ generating ? "Generating..." : "Generate API Key" }}
        </base-button>
      </div>
    </div>

    <base-alert v-if="apiKeys.length === 0" type="warning" class="info-min">
      <i class="flaticon-warning mr-1"></i>
      No API keys yet. Generate one to enable API access.
    </base-alert>

    <b-table
      v-else
      :items="apiKeys"
      :fields="fields"
      hover
      responsive
      show-empty
      empty-text="No API keys found"
      class="mt-2"
      size="sm"
    >
      <template #cell(status)="{ item }">
        <span
          class="badge-status"
          :class="{
            'badge-success': item.status === 'ACTIVE',
            'badge-danger':
              item.status === 'REVOKED' || item.status === 'EXPIRED'
          }"
        >
          {{ item.status }}
        </span>
      </template>

      <template #cell(apiKey)="{ item }">
        <span class="text-monospace">{{ item.apiKey }}</span>
      </template>

      <template #cell(createdAt)="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template #cell(updatedAt)="{ item }">
        {{ formatDate(item.updatedAt) }}
      </template>

      <template #cell(actions)="{ item }">
        <el-tooltip content="Revoke" placement="bottom">
          <span class="icon-action text-danger" @click="revokeApiKey(item._id)">
            <i class="flaticon-delete"></i>
          </span>
        </el-tooltip>
      </template>
    </b-table>

    <modal
      :show.sync="showKeyModal"
      @close="onModalClose"
      size="md"
      no-close-on-backdrop
    >
      <template slot="header">
        <h2 class="subtitle mb-0">API Key Generated</h2>
      </template>

      <div class="text-center py-2">
        <div class="mb-3">
          <i class="flaticon-check-2 text-success f-36 d-block mb-2"></i>
          <p class="mb-0">Your API Key has been successfully created.</p>
        </div>

        <base-alert type="warning" class="text-left mb-3">
          <i class="flaticon-warning mr-1"></i>
          <strong>Copy it now.</strong> For security reasons this key will not
          be shown again. If it is ever exposed or compromised, revoke it
          immediately from this panel.
        </base-alert>

        <code class="d-block p-2 bg-light border rounded f-12 mb-2 text-dark">
          {{ generatedKey }}
          <el-tooltip content="Copy to clipboard" placement="top">
            <i
              class="flaticon-copy curp ml-1"
              v-clipboard:copy="generatedKey"
              v-clipboard:success="onCopy"
              v-clipboard:error="onErrorCopy"
            ></i>
          </el-tooltip>
        </code>
      </div>

      <template slot="footer">
        <base-button type="primary" size="sm" @click="showKeyModal = false">
          Close
        </base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { IntegrationAccessAPI } from "../../../core/api";
import { formatDate } from "../services/format.service";

export default {
  name: "ResellerApiKeys",
  props: {
    resellerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      apiKeys: [],
      fields: [
        { key: "status", label: "Status" },
        { key: "apiKey", label: "Key" },
        { key: "description", label: "Description" },
        { key: "createdAt", label: "Created At" },
        { key: "updatedAt", label: "Updated At" },
        { key: "actions", label: "" }
      ],
      showKeyModal: false,
      generatedKey: "",
      generating: false
    };
  },
  async created() {
    await this.loadApiKeys();
  },
  methods: {
    async loadApiKeys() {
      try {
        let { data } = await IntegrationAccessAPI.getApiKeys(
          this.resellerId,
          this.sesion.token
        );
        this.apiKeys = data ? data : [];
      } catch (error) {
        this.apiKeys = [];
      }
    },
    async generateApiKey() {
      this.generating = true;
      try {
        let { data } = await IntegrationAccessAPI.generateApiKey(
          { id: this.resellerId },
          this.sesion.token
        );
        if (data) {
          await this.loadApiKeys();
          this.generatedKey = data.apiKey;
          this.showKeyModal = true;
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "The API Key could not be generated. Please try again",
            icon: "flaticon-remove"
          });
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: "The API Key could not be generated. Please try again",
          icon: "flaticon-remove"
        });
      } finally {
        this.generating = false;
      }
    },
    async revokeApiKey(keyId) {
      let { value } = await Swal.fire({
        title: "Revoke API Key",
        html:
          "Are you sure? The reseller will lose API access for this key immediately.",
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
        buttonsStyling: false,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        cancelButtonText: "No",
        confirmButtonText: "Yes"
      });
      if (value) {
        await this.revokeApiKeyByReseller(this.resellerId, keyId);
      }
    },
    async revokeApiKeyByReseller(resellerId, keyId) {
      try {
        let { data } = await IntegrationAccessAPI.revokeApiKey(
          resellerId,
          keyId,
          this.sesion.token
        );
        if (data) {
          this.$notify({
            type: "success",
            title: "Success!",
            message: "The API Key has been revoked",
            icon: "flaticon-check-3"
          });
          await this.loadApiKeys();
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "The API Key could not be revoked. Please try again",
            icon: "flaticon-remove"
          });
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: "The API Key could not be revoked. Please try again",
          icon: "flaticon-remove"
        });
      }
    },
    onModalClose() {
      this.generatedKey = "";
    },
    onCopy() {
      this.$notify({
        type: "success",
        title: "Copied!",
        message: "API Key copied to clipboard",
        icon: "flaticon-check-3"
      });
    },
    onErrorCopy() {
      this.$notify({
        type: "danger",
        title: "Error!",
        message: "Could not copy the API Key. Please copy it manually.",
        icon: "flaticon-remove"
      });
    },
    formatDate
  },
  computed: {
    ...mapGetters(["sesion"])
  }
};
</script>
